import type { ComputedRef } from 'vue'
import type { OrderDetails } from '@widgets/orders'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { getCustomerUrl } from '@shared/lib/routes'
import { formatPrice, useDayjs } from '@shared/lib/format'

import { useOrderStore } from './store'

const NEVER = 'Never'

interface OrderInfoRow {
  label: string
  value: string
}

interface OrderInfoReturn {
  refOrder: ComputedRef<OrderDetails | null>
  refPaymentType: ComputedRef<string>
  refPaymentNumber: ComputedRef<string | null>
  refDiscount: ComputedRef<string | null>
  refTotal: ComputedRef<string>
  refCreditsUsed: ComputedRef<string>
  refCustomerText: ComputedRef<string>
  refCustomerUrl: ComputedRef<string | null>
  refOtherRows: ComputedRef<OrderInfoRow[]>
  formatPrice: (cents: number) => string
}

/** The "Order info" card of the order page; values and empty cases mirror the legacy page. */
export const useOrderInfo = (): OrderInfoReturn => {
  const orderStore = useOrderStore()
  const dayjs = useDayjs()

  const refOrder = computed<OrderDetails | null>(() => orderStore.getOrder)

  const formatDate = (value: string): string => dayjs(value).format(DateFormat.KanbanDate)

  /** The legacy page named the payment by whether it belongs to a subscription, not by the order type. */
  const refPaymentType = computed<string>(() => (refOrder.value?.subscription ? 'Installment' : 'One time payment'))

  const refPaymentNumber = computed<string | null>(() => {
    const order = refOrder.value

    return order?.subscription ? `${order.number ?? ''}/${order.subscription.parts ?? ''}` : null
  })

  const refDiscount = computed<string | null>(() => (refOrder.value?.discount ? `${refOrder.value.discount}%` : null))
  const refTotal = computed<string>(() => `${formatPrice(refOrder.value?.total ?? 0)} USD`)
  const refCreditsUsed = computed<string>(() => `${formatPrice(refOrder.value?.credits_used ?? 0)} USD`)

  const refCustomerText = computed<string>(() => {
    const customer = refOrder.value?.customer

    return customer ? `${customer.full_name} / #${customer.id}` : ''
  })
  const refCustomerUrl = computed<string | null>(() => {
    const customer = refOrder.value?.customer

    return customer ? getCustomerUrl(customer.id) : null
  })

  const refOtherRows = computed<OrderInfoRow[]>(() => {
    const order = refOrder.value
    if (!order) return []

    const rows: OrderInfoRow[] = [
      { label: 'Created at:', value: formatDate(order.created_at) },
      { label: 'Order:', value: `#${order.id}` },
    ]

    if (order.subscription) {
      rows.push({ label: 'Subscription:', value: `#${order.subscription.id}` })
    }

    rows.push({ label: 'Expires at:', value: order.expires_at ? formatDate(order.expires_at) : NEVER })

    return rows
  })

  return {
    refOrder,
    refPaymentType,
    refPaymentNumber,
    refDiscount,
    refTotal,
    refCreditsUsed,
    refCustomerText,
    refCustomerUrl,
    refOtherRows,
    formatPrice,
  }
}
